import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ErrorResponse } from '../api/hscan';
import {
  SignUpPresetResponse,
  requestSignUpForShareToPatient,
  requestVerifyOnSiteLink,
  requestShareToPatientChallenge,
  ShareToPatientChallenge,
  requestShareToPatientAnswer,
  requestShareToPatientLink,
  ShareToPatientLink,
  SolveChallengeRequest,
  QrCodeLink,
  requestQrCodeLink,
  QrChallenge,
  requestCreateQrChallenge,
  requestSolveQrChallenge,
  RequestStoreDelegateAgreementParams,
  RequestStoreDelegateVerificationParams,
  requestStoreDelegateAgreement,
  requestStoreDelegateVerification,
  StoreDelegateLink,
  requestStoreDelegateLink,
  RequestStoreDelegateVerificationBody,
} from '../api/hscan/link';

import { MutationOption, QueryOption } from './react-query-type';

export const useVerifyOnSiteLink = (
  options?: MutationOption<string, unknown, AxiosError<ErrorResponse>>,
) => {
  return useMutation({ mutationFn: requestVerifyOnSiteLink, ...options });
};

// ======= share-to-patient =======

export const useSignUpForShareToPatient = (
  options?: MutationOption<
    string,
    SignUpPresetResponse,
    AxiosError<ErrorResponse>
  >,
) => {
  const fetch = async (uri: string) => {
    const { data } = await requestSignUpForShareToPatient(uri);
    return data;
  };
  return useMutation({ mutationFn: fetch, ...options });
};

export const useShareToPatientLink = (
  uri: string,
  options?: QueryOption<
    ShareToPatientLink,
    ShareToPatientLink,
    AxiosError<ErrorResponse>
  >,
) => {
  const fetch = async () => {
    const { data } = await requestShareToPatientLink(uri);
    return data;
  };

  return useQuery({
    queryKey: ['SharedPatientLink', uri],
    queryFn: fetch,
    ...options,
  });
};

export const useShareToPatientChallenge = (
  options?: MutationOption<string, ShareToPatientChallenge>,
) => {
  const fetch = async (uri: string) => {
    const { data } = await requestShareToPatientChallenge(uri);
    return data;
  };
  return useMutation({ mutationFn: fetch, ...options });
};

export const useShareToPatientAnswer = (
  options?: MutationOption<SolveChallengeRequest, ShareToPatientChallenge>,
) => {
  const fetch = async (params: SolveChallengeRequest) => {
    const { data } = await requestShareToPatientAnswer(params);
    return data;
  };
  return useMutation({ mutationFn: fetch, ...options });
};

// ======= QR code =======

export const useQrCodeLink = (
  uri: string,
  options?: QueryOption<QrCodeLink, QrCodeLink, AxiosError<ErrorResponse>>,
) => {
  return useQuery({
    queryKey: ['QrCodeLink', uri],
    async queryFn() {
      const { data } = await requestQrCodeLink(uri);
      return data;
    },
    ...options,
  });
};

export const useCreateQrChallenge = (
  options?: MutationOption<string, QrChallenge>,
) => {
  return useMutation({
    mutationFn: async uri => {
      const { data } = await requestCreateQrChallenge(uri);
      return data;
    },
    ...options,
  });
};

export const useSolveQrChallenge = (
  options?: MutationOption<SolveChallengeRequest, QrChallenge>,
) => {
  return useMutation({
    mutationFn: async params => {
      const { data } = await requestSolveQrChallenge(params);
      return data;
    },
    ...options,
  });
};

// ======= store-delegate =======

export const useStoreDelegateLink = (
  uri: string,
  options?: QueryOption<
    StoreDelegateLink,
    StoreDelegateLink,
    AxiosError<ErrorResponse>
  >,
) => {
  return useQuery({
    queryKey: ['StoreDelegateLink', uri],
    queryFn: async () => {
      const { data } = await requestStoreDelegateLink(uri);
      return data;
    },
    ...options,
  });
};

export const useAgreeStoreDelegate = (
  options?: MutationOption<
    RequestStoreDelegateAgreementParams,
    unknown,
    AxiosError<ErrorResponse>
  >,
) => {
  return useMutation({
    mutationFn: async params => {
      const { data } = await requestStoreDelegateAgreement(params);
      return data;
    },
    ...options,
  });
};

export const useVerifyStoreDelegate = (
  options?: MutationOption<
    RequestStoreDelegateVerificationParams &
      RequestStoreDelegateVerificationBody,
    unknown,
    AxiosError<ErrorResponse>
  >,
) => {
  return useMutation({
    mutationFn: async params => {
      const { data } = await requestStoreDelegateVerification(params);
      return data;
    },
    ...options,
  });
};
